import React, { useEffect, useState } from "react";
import { message } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import gmail from "Assets/SocialMediaIconSvg/new-gmail.svg";
import { emailPattern } from "Utility/Constants";
import { getForgotPasswordErrorMessage } from "Utility/authErrors";
import Logo from "@/Assets/Logo/logo.png";
import AuthImage from "@/Assets/auth-image.png";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/Components/ui/field";
import { Loader2 } from "lucide-react";

function getLegacyQueryValue(search, key) {
  const marker = `${key}=`;
  if (!search?.includes(marker)) return "";
  return search.split(marker)?.[1]?.split("+")?.[0] || "";
}

export const isGoogleAccountByEmail = async (email) => {
  try {
    await Auth.signIn(email, "dummy-password-check-xyz");
  } catch (error) {
    console.log("isGoogleAccount check:", error.code, error.message);

    if (error.code === "UserNotFoundException") {
      // No account exists with this email at all
      return false;
    }

    if (error.code === "NotAuthorizedException") {
      // Account exists — now check if it's a password-based or Google account
      // Google users in Cognito cannot be signed in with password,
      // and their error message differs slightly
      if (
        error.message?.includes(
          "Unable to login because of security reasons",
        ) ||
        error.message?.includes("social") ||
        error.message?.includes("Google")
      ) {
        return true;
      }

      // Normal user with wrong password — not a Google account
      return false;
    }

    // Any other error — assume not Google, let forgotPassword handle it
    return false;
  }

  // signIn succeeded with dummy password (extremely unlikely) — not Google
  return false;
};

export const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("email");

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const qpCode =
      params.get("code") || getLegacyQueryValue(location.search, "code");
    const qpEmail =
      params.get("email") || getLegacyQueryValue(location.search, "email");

    if (qpEmail) setEmail(qpEmail);
    if (qpCode) {
      setCode(qpCode);
      setCurrentStep("reset");
    }
  }, [location.search]);

  const handleSendCode = async (e) => {
    e.preventDefault();

    const normalizedEmail = String(email || "").trim();
    if (!normalizedEmail || !emailPattern.test(normalizedEmail)) {
      message.error("Please enter a valid email.");
      return;
    }

    setIsLoading(true);

    const isGoogleUser = await isGoogleAccountByEmail(normalizedEmail);
    if (isGoogleUser) {
      message.error(
        "This account uses Google Sign-In. Password reset is not available.",
      );
      return;
    }
    try {
      await Auth.forgotPassword(normalizedEmail);
      message.success("Verification code has been sent to your email");
      setEmail(normalizedEmail);
      setCurrentStep("reset");
    } catch (err) {
      message.error(getForgotPasswordErrorMessage(err));
      if (
        err?.code === "UserNotFoundException" ||
        err?.message === "Username/client id combination not found."
      ) {
        setCurrentStep("email");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    const normalizedEmail = String(email || "").trim();
    const normalizedCode = String(code || "").trim();

    if (!normalizedEmail || !emailPattern.test(normalizedEmail)) {
      message.error("Please enter a valid email.");
      return;
    }
    if (!normalizedCode) {
      message.error("Please enter verification code.");
      return;
    }
    if (!password || password.length < 8) {
      message.error("Password must be minimum 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      message.error("Passwords must match.");
      return;
    }

    setIsLoading(true);
    try {
      await Auth.forgotPasswordSubmit(
        normalizedEmail,
        normalizedCode,
        password,
      );
      message.success("Your password has been successfully reset");
      navigate("/login");
    } catch (err) {
      message.error(err?.message || "Could not reset password.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            aria-label="Inprofile home"
          >
            <img src={Logo} alt="Inprofile" className="h-8 w-8" />
            Inprofile
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            {currentStep === "email" ? (
              <form className="flex flex-col gap-6" onSubmit={handleSendCode}>
                <FieldGroup>
                  <div className="flex flex-col items-center gap-1 text-center">
                    <h1 className="text-2xl font-bold">Reset your password</h1>
                    <p className="text-muted-foreground text-sm text-balance">
                      Enter your email and we&apos;ll send a verification code
                    </p>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="email">Email</FieldLabel>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      autoComplete="email"
                      required
                    />
                  </Field>
                  <Field>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Reset Code"
                      )}
                    </Button>
                    <FieldDescription className="text-center">
                      Remember your password?{" "}
                      <Link
                        to="/login"
                        className="underline underline-offset-4 text-primary"
                      >
                        Sign in
                      </Link>
                    </FieldDescription>
                  </Field>
                </FieldGroup>
              </form>
            ) : (
              <form
                className="flex flex-col gap-6"
                onSubmit={handleResetPassword}
              >
                <FieldGroup>
                  <div className="flex flex-col items-center gap-1 text-center">
                    <h1 className="text-2xl font-bold">Create new password</h1>
                    <p className="text-muted-foreground text-sm text-balance">
                      Enter the verification code and your new password
                    </p>
                  </div>
                  <div className="flex justify-start">
                    <a
                      href="https://mail.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors hover:text-black"
                    >
                      <img src={gmail} alt="Gmail" className="mr-2 w-7 h-7" />
                      Open Gmail
                    </a>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="reset-email">Email</FieldLabel>
                    <Input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      autoComplete="email"
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="verification-code">
                      Verification Code
                    </FieldLabel>
                    <Input
                      id="verification-code"
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter 6 digit code"
                      value={code}
                      onChange={(e) =>
                        setCode(
                          e.target.value.replace(/[^0-9]/g, "").slice(0, 6),
                        )
                      }
                      disabled={isLoading}
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="new-password">New Password</FieldLabel>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter your new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="confirm-password">
                      Confirm Password
                    </FieldLabel>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                  </Field>
                  <Field>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                    <FieldDescription className="text-center">
                      Remember your password?{" "}
                      <Link
                        to="/login"
                        className="underline underline-offset-4 text-primary"
                      >
                        Sign in
                      </Link>
                    </FieldDescription>
                  </Field>
                </FieldGroup>
              </form>
            )}
          </div>
        </div>
      </div>
      <div className="bg-[#f2f5f8] relative hidden lg:block">
        <img
          src={AuthImage}
          alt=""
          className="absolute inset-0 h-full w-full object-contain dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
};
